import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import UtilController from '../controllers/UtilController';
import { statusDispatch } from './globalActions';
import {
  GenericPaginationModelAPIParams,
  IGetPublicRatingItems,
  ILongContractAttributes,
  INearbyLocation,
  IPharmacyRatesProfessional,
  IRatePharmacyParams,
  ISmartRegisterParams,
} from '../models';
import { AppIDType } from '../models/IAppID';
import { ILongTermUtils, ISystemDetailParams } from '../models/UtilModel';
import { Agencies, Language } from '../helpers/Constants';

export const utilActions = {
  contacts: 'util/CONTACTS',
  languages: 'util/LANGUAGES',
  setLoading: 'util/set/loading',
  getPublicKey: 'util/get/publicKey',
  registerSmartLogin: 'util/register/smartLogin',
  setBiometricEnabled: 'util/set/isBiometricEnabled',
  deRegisterSmartLogin: 'util/deregister/smartLogin',
  setTourCompleted: 'util/set/isTourCompleted',
  setShowBiometric: 'util/set/showBiometric',
  setNearbyProps: 'util/set/nearbyProps',
  checkSystemDetails: 'util/check/systemDetails',
  setSelectedIds: 'util/set/selectedIds',
  setShiftContractAttributes: 'util/set/shift-contract-attributes',
  resetShiftContractAttributes: 'util/reset/shift-contract-attributes',
  setSelectionMode: 'reports/set/selection',
  getPubliclyAvailableShifts: 'util/get/public-shifts',
  getPublicProfessionalRatingItems: 'util/get/professional/public-rating-items',
  getPublicPharmacyRatingItems: 'util/get/pharmacy/public-rating-items',
  getPublicPharmacistRatingItems: 'util/get/pharmacist/public-rating-items',
  publicRateProfessional: 'util/rate/professional',
  publicRatePharmacist: 'util/rate/pharmacist',
  publicRatePharmacy: 'util/rate/pharmacy',
  setLongTermContractAttributes: 'util/set/long-term-attributes',
  utilResetValue: 'util/reset/value',
  setBookedContractDates: 'util/set/booked-dates',
  setInitialLandingURL: 'util/set/initial-landing-url',
};

export const getContactInfo = createAsyncThunk(
  utilActions.contacts,
  async (_, { rejectWithValue }) => {
    try {
      const resp = await UtilController.getContactInfo();
      return statusDispatch(resp, rejectWithValue);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getLanguagesInfo = createAsyncThunk(
  utilActions.languages,
  async (_, { rejectWithValue }) => {
    try {
      const resp = await UtilController.getLanguagesInfo();
      return statusDispatch(resp, rejectWithValue);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const setLoading = createAction<Array<string>>(utilActions.setLoading);
export const getPublicKey = createAsyncThunk(
  utilActions.getPublicKey,
  async (deviceId: string, { rejectWithValue }) => {
    try {
      const resp = await UtilController.getPublicKey(deviceId);
      return statusDispatch(resp, rejectWithValue);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const registerSmartLogin = createAsyncThunk(
  utilActions.registerSmartLogin,
  async (params: ISmartRegisterParams, { rejectWithValue }) => {
    try {
      const resp = await UtilController.registerSmartLogin(params);
      return statusDispatch(resp, rejectWithValue);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const setBiometricEnabled = createAction<boolean | string | null>(
  utilActions.setBiometricEnabled
);

export const setTourCompleted = createAction<boolean | null>(
  utilActions.setTourCompleted
);

export const setShowBiometric = createAction<boolean>(
  utilActions.setShowBiometric
);

export const deregisterSmartLogin = createAsyncThunk(
  utilActions.deRegisterSmartLogin,
  async (params: { totp: string; userHash: string }, { rejectWithValue }) => {
    try {
      const resp = await UtilController.deregisterSmartLogin(params);
      return statusDispatch(resp, rejectWithValue);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const checkSystemDetails = createAsyncThunk(
  utilActions.checkSystemDetails,
  async (params: ISystemDetailParams, { rejectWithValue }) => {
    try {
      const resp = await UtilController.checkSystemDetails(params);
      return statusDispatch(resp, rejectWithValue);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getPubliclyAvailableShifts = createAsyncThunk(
  utilActions.getPubliclyAvailableShifts,
  async (params: GenericPaginationModelAPIParams, { rejectWithValue }) => {
    try {
      const resp = await UtilController.getPubliclyAvailableShifts(params);
      return statusDispatch(resp, rejectWithValue);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getPublicProfessionalRatingItems = createAsyncThunk(
  utilActions.getPublicProfessionalRatingItems,
  async (
    params: IGetPublicRatingItems & { locale?: Language },
    { rejectWithValue }
  ) => {
    try {
      const resp = await UtilController.getPublicProfessionalRatingItems(
        params
      );
      return statusDispatch(resp, rejectWithValue);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getPublicPharmacyRatingItems = createAsyncThunk(
  utilActions.getPublicPharmacyRatingItems,
  async (params: IGetPublicRatingItems, { rejectWithValue }) => {
    try {
      const resp = await UtilController.getPublicPharmacyRatingItems(params);
      return statusDispatch(resp, rejectWithValue);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const publicRateProfessional = createAsyncThunk(
  utilActions.publicRateProfessional,
  async (params: IPharmacyRatesProfessional, { rejectWithValue }) => {
    try {
      const resp = await UtilController.publicRateProfessional(params);
      return statusDispatch(resp, rejectWithValue);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const publicRatePharmacist = createAsyncThunk(
  utilActions.publicRatePharmacist,
  async (params: IPharmacyRatesProfessional, { rejectWithValue }) => {
    try {
      const resp = await UtilController.publicRatePharmacist(params);
      return statusDispatch(resp, rejectWithValue);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const publicRatePharmacy = createAsyncThunk(
  utilActions.publicRatePharmacy,
  async (params: IRatePharmacyParams, { rejectWithValue }) => {
    try {
      const resp = await UtilController.publicRatePharmacy(params);
      return statusDispatch(resp, rejectWithValue);
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const setNearbyProps = createAction<INearbyLocation>(
  utilActions.setNearbyProps
);

export const setSelectedIds = createAction<Array<string>>(
  utilActions.setSelectedIds
);

export const resetLongTermContractAttributes = createAction(
  utilActions.resetShiftContractAttributes
);

export const setInitialLandingURL = createAction<string>(
  utilActions.setInitialLandingURL
);

export const setSelectionMode = createAction<{
  pharmacyId: string;
  showCheckbox: boolean;
  isAutoSyncRecord?: boolean;
  activeView?: string;
}>(utilActions.setSelectionMode);

export const setLongTermContractAttributes = createAction<ILongTermUtils>(
  utilActions.setLongTermContractAttributes
);

export const utilResetValue = createAction<Array<string>>(
  utilActions.utilResetValue
);
export const setBookedContractDates = createAction<Array<string>>(
  utilActions.setBookedContractDates
);
